import { CSSProperties } from 'react';
import { Color, Size, Styles } from '@vivli/shared/theme';
import { DatePickerFormFieldComponent, TextAreaFormFieldComponent, TextFormFieldComponent } from '@vivli/shared/components';
import { usePublishApproved } from '@vivli/features/data-requests/infrastructure/hook';
import { IDataRequest } from '@vivli/features/data-requests/infrastructure/interface';
import { useAppConfigContext } from '@vivli/core/infrastructure/context';
import { PublishCheckboxComponent } from './publish-checkbox.component';
import { PublishingRequestedStudyRowFormComponent } from './publishing-requested-study-row-form.component';
import { PublishingRequestedStudyRowPreviewComponent } from './publishing-requested-study-row-preview.component';
import { CitationsContextWrapper } from '@vivli/shared/features/citations/infrastructure/wrappers';
import moment from 'moment';
import { DisclosuresListComponent } from './disclosures-list.component';

const vivliPrivateFieldStyle: CSSProperties = {
    backgroundColor: 'rgb(255, 246, 217)',
};
const vivliPrivateFieldWideStyle: CSSProperties = {
    backgroundColor: 'rgb(255, 246, 217)',
    width: '70%',
};
const sectionStyle: CSSProperties = {
    overflowX: 'hidden',
    overflowY: 'auto',
    paddingLeft: 0,
    paddingRight: 0,
    display: 'flex',
    flexDirection: 'row',
    paddingBottom: 5,
    paddingTop: 0,
};

const sectionStyleContainerConfig: CSSProperties = {
    ...sectionStyle,
    display: 'grid',
    justifyContent: 'space-around',
    gridTemplateColumns: '48px 1fr',
    columnGap: '48px',
};

const sectionStyleForConfigEdits: CSSProperties = {
    ...sectionStyle,
    marginBottom: 0,
};

const singleLineContainerStyle: CSSProperties = {
    display: 'grid',
    gridAutoFlow: 'column',
    paddingBottom: 0,
    paddingTop: Size.INSET_SIZE / 2.8,
    color: Color.VIVLI_PUBLISHED_DARKBLUE,
    whiteSpace: 'pre-wrap',
};

const singleLineHeaderStyle: CSSProperties = {
    fontWeight: 'bold',
    marginRight: '10px',
};

const paragraphContainerStyle: CSSProperties = {
    display: 'grid',
    gridAutoFlow: 'row',
    paddingBottom: 0,
    paddingTop: Size.INSET_SIZE / 2.8,
    color: Color.VIVLI_PUBLISHED_DARKBLUE,
    whiteSpace: 'pre-wrap',
    lineHeight: '1.25',
};

const paragraphHeaderStyle: CSSProperties = {
    fontWeight: 'bold',
    marginRight: '10px',
    marginBottom: Size.INSET_SIZE / 3,
    minWidth: 180,
};

const listSectionStyle: CSSProperties = {
    ...paragraphContainerStyle,
    marginLeft: '0',
};

interface PublishingContentComponentProps {
    dataRequest: IDataRequest;
    isConfigMode: boolean;
    arrayNameStudies?: string;
    fieldsStudies?: any;
    arrayNameDisclosures?: string;
    fieldsDisclosures?: any;
}

export const PublishingContentComponent = ({
    dataRequest,
    isConfigMode,
    arrayNameStudies,
    fieldsStudies,
    arrayNameDisclosures,
    fieldsDisclosures,
}: PublishingContentComponentProps) => {
    const { getLeadInvestigator, sectionsToShow, getFundingSources, getDataRequestIdToShow } = usePublishApproved();
    const { bannerConfig } = useAppConfigContext();

    const containerStyle: CSSProperties = {
        overflowX: 'hidden',
        overflowY: 'auto',
        flex: 'auto',
        flexDirection: 'column',
        height: '100%',
        paddingTop: Size.INSET_SIZE,
        paddingBottom: Size.INSET_SIZE,
        paddingLeft: isConfigMode ? 0 : Size.INSET_SIZE * 1.5,
        paddingRight: isConfigMode ? 0 : Size.INSET_SIZE * 1.5,
    };

    const titleDisplayContainerStyle: CSSProperties = {
        overflow: 'hidden',
        backgroundImage: `url(${bannerConfig?.publicApprovedBackgroundUrl})`,
        backgroundSize: 'cover',
        display: 'grid',
        position: 'relative',
        height: '100%',
        fontSize: Size.FontSize.Larger * 2,
        alignItems: 'center',
        justifyContent: 'center',
    };

    const titleTextStyle: CSSProperties = {
        display: 'grid',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'white',
        fontSize: Size.FontSize.Larger * 2,
        alignSelf: 'center',
        maxWidth: '100%',
        paddingLeft: Size.INSET_SIZE * 2,
        paddingRight: Size.INSET_SIZE * 2,
    };

    const titleTextStyleConfig: CSSProperties = {
        ...titleTextStyle,
        color: 'black',
        fontWeight: 'bold',
        fontSize: Size.FontSize.Larger,
    };

    const editFieldsStyleToUse = isConfigMode ? sectionStyleForConfigEdits : sectionStyle;
    const paragraphSectionStyleToUse = isConfigMode ? sectionStyleContainerConfig : sectionStyle;

    const piInfo = getLeadInvestigator(dataRequest);
    const idInfoToShow = getDataRequestIdToShow(dataRequest);
    const fundingSource = getFundingSources(dataRequest);
    const sections = sectionsToShow(dataRequest, isConfigMode);
    const sectionMap = sections.reduce((map, section) => {
        map[section.name] = section;
        return map;
    }, {});

    return (
        <div style={containerStyle} className="scrolly">
            <div>
                {!isConfigMode && (
                    <div style={titleDisplayContainerStyle}>
                        <div style={titleTextStyle}>
                            <p>{dataRequest.requestTitle}</p>
                        </div>
                    </div>
                )}
                {isConfigMode && (
                    <div style={titleTextStyleConfig}>
                        <p>{dataRequest.requestTitle}</p>
                    </div>
                )}
            </div>

            {sectionMap['leadInvestigator'].show && (
                <div style={sectionStyle}>
                    {isConfigMode && <PublishCheckboxComponent name={'publishing.publishingRules.showLeadInvestigator'} />}
                    <div style={singleLineContainerStyle}>
                        <span style={singleLineHeaderStyle}>Lead Investigator:</span>
                        <span>{piInfo}</span>
                    </div>
                </div>
            )}
            {sectionMap['titleOfProposedResearch'].show && (
                <div style={sectionStyle}>
                    {isConfigMode && (
                        <div style={{ paddingRight: 0 }}>
                            <PublishCheckboxComponent
                                name={'publishing.publishingRules.showTitleOfProposedResearch'}
                            ></PublishCheckboxComponent>
                        </div>
                    )}
                    <div style={singleLineContainerStyle}>
                        <div style={singleLineHeaderStyle}>Title Of Proposed Research:</div>
                        <div>{dataRequest.requestTitle}</div>
                    </div>
                </div>
            )}

            {sectionMap['vivliDataRequestIdInfo'].show && (
                <div style={sectionStyle}>
                    {isConfigMode && <PublishCheckboxComponent name={'publishing.publishingRules.showVivliDataRequestIdInfo'} />}
                    <div style={singleLineContainerStyle}>
                        <span style={singleLineHeaderStyle}>Vivli Data Request:</span>
                        {!isConfigMode && <span>{idInfoToShow}</span>}
                    </div>
                    {isConfigMode && (
                        <TextFormFieldComponent
                            name={'publishing.publishingData.vivliDataRequestIdInfo'}
                            label={''}
                            style={vivliPrivateFieldStyle}
                            defaultValue={idInfoToShow}
                        />
                    )}
                </div>
            )}

            {sectionMap['fundingSource'].show && (
                <div style={sectionStyle}>
                    {isConfigMode && (
                        <div style={{ paddingRight: 0 }}>
                            <PublishCheckboxComponent name={'publishing.publishingRules.showFundingSource'}></PublishCheckboxComponent>
                        </div>
                    )}
                    {/*{isConfigMode && <PublishCheckboxComponent name={'publishing.publishingRules.showFundingSource'} />}*/}
                    <div style={paragraphContainerStyle}>
                        <span style={paragraphHeaderStyle}>Funding Source:</span>
                        {<span>{fundingSource}</span>}
                    </div>
                </div>
            )}
            {sectionMap['potentialConflictsOfInterest'].show && (
                <div style={paragraphSectionStyleToUse}>
                    {isConfigMode && <PublishCheckboxComponent name={'publishing.publishingRules.showPotentialConflictsOfInterest'} />}
                    <div style={paragraphContainerStyle}>
                        <span style={paragraphHeaderStyle}>Potential Conflicts of Interest:</span>
                        {<span>{dataRequest?.researchTeam?.leadResearcherTeamMemberDetail?.conflictsOfInterest}</span>}
                    </div>
                </div>
            )}
            {sectionMap['laySummary'].show && (
                <div style={paragraphSectionStyleToUse}>
                    {isConfigMode && <PublishCheckboxComponent name={'publishing.publishingRules.showLaySummary'} />}
                    <div style={paragraphContainerStyle}>
                        <span style={paragraphHeaderStyle}>Summary of the Proposed Research:</span>
                        <span>{dataRequest.researchProposal.laySummary}</span>
                    </div>
                </div>
            )}
            {sectionMap['statisticalAnalysisPlan'].show && (
                <div style={paragraphSectionStyleToUse}>
                    {isConfigMode && <PublishCheckboxComponent name={'publishing.publishingRules.showStatisticalAnalysisPlan'} />}
                    <div style={paragraphContainerStyle}>
                        <span style={paragraphHeaderStyle}>Statistical Analysis Plan:</span>
                        <span>{dataRequest.statisticalAnalysisPlan.description}</span>
                    </div>
                </div>
            )}
            {/*/=requested Studies section*/}
            <div style={{ ...paragraphContainerStyle, marginBottom: '-10', marginTop: '-10' }}>
                <div style={paragraphContainerStyle}>
                    <span style={{ ...paragraphHeaderStyle, marginBottom: 0 }}>Requested Studies:</span>
                </div>
                <div style={listSectionStyle}>
                    {isConfigMode &&
                        fieldsStudies?.map((field, index) => (
                            <PublishingRequestedStudyRowFormComponent
                                key={field.idRequestedStudies}
                                index={index}
                                arrayName={arrayNameStudies}
                                isConfigMode={true}
                            />
                        ))}
                    {!isConfigMode &&
                        dataRequest?.requestedStudies?.map((s, index) => (
                            <div key={index} style={Styles.FORM_ROW}>
                                <PublishingRequestedStudyRowPreviewComponent requestedStudy={s} index={index} isConfigMode={false} />
                            </div>
                        ))}
                </div>
            </div>
            {sectionMap['additionalInformation'].show && (
                <div style={editFieldsStyleToUse}>
                    {isConfigMode && <PublishCheckboxComponent name={'publishing.publishingRules.showAdditionalInformation'} />}
                    <div style={paragraphContainerStyle}>
                        <span style={paragraphHeaderStyle}>Additional Information:</span>
                        {!isConfigMode && <span>{dataRequest.publishing?.publishingData?.additionalInformation}</span>}
                    </div>
                    {isConfigMode && (
                        <TextAreaFormFieldComponent
                            name={'publishing.publishingData.additionalInformation'}
                            label={''}
                            style={vivliPrivateFieldWideStyle}
                        />
                    )}
                </div>
            )}

            {/*public disclosures section*/}
            {sectionMap['publicDisclosures'].show && (
                <CitationsContextWrapper objectId={dataRequest.id} doiStem={dataRequest.doiStem} isStudy={false} doUpdate={false}>
                    <DisclosuresListComponent
                        fieldsDisclosures={fieldsDisclosures}
                        dataRequest={dataRequest}
                        isConfigMode={isConfigMode}
                        arrayName={arrayNameDisclosures}
                    />
                </CitationsContextWrapper>
            )}

            {sectionMap['summaryOfResults'].show && (
                <div style={{ ...editFieldsStyleToUse, marginTop: 10 }}>
                    {isConfigMode && <PublishCheckboxComponent name={'publishing.publishingRules.showSummaryOfResults'} />}
                    <div style={paragraphContainerStyle}>
                        <span style={paragraphHeaderStyle}>Summary of Results:</span>
                        {!isConfigMode && <span>{dataRequest.publishing?.publishingData?.summaryOfResults}</span>}
                    </div>
                    {isConfigMode && (
                        <TextAreaFormFieldComponent
                            name={'publishing.publishingData.summaryOfResults'}
                            label={''}
                            style={vivliPrivateFieldWideStyle}
                        />
                    )}
                </div>
            )}

            {sectionMap['withdrawn'].show && (
                <div style={{ ...sectionStyle, marginTop: 10 }}>
                    {isConfigMode && <PublishCheckboxComponent name={'publishing.publishingRules.showWithdrawn'} />}
                    <div style={singleLineContainerStyle}>
                        <span style={singleLineHeaderStyle}>This request was withdrawn on:</span>
                        {!isConfigMode && (
                            <span>{moment(dataRequest.publishing?.publishingData?.actualWithdrawnDate).format('DD-MMM-yyyy')}</span>
                        )}
                    </div>
                    {isConfigMode && (
                        <DatePickerFormFieldComponent
                            name={'publishing.publishingData.actualWithdrawnDate'}
                            label={''}
                            optClassName="vivliPrivateDateFieldStyle"
                            style={vivliPrivateFieldStyle}
                            dateFormat={`dd-MMM-yyyy`}
                        />
                    )}
                </div>
            )}
        </div>
    );
};
